<script>
  import { workspace } from '../lib/stores/workspace.js';
  import { showToast } from '../lib/stores/toast.js';
  import { supabase } from '../lib/supabase.js';
  import TechJourneyEditor from './TechJourneyEditor.svelte';
  import TechCanvas from './tech/TechCanvas.svelte';

  let pages = $state([]);
  let journeys = $state([]);
  let loading = $state(true);
  let editorOpen = $state(false);

  let activeJourneyIds = $state([]);
  let pickerOpen = $state(false);
  let draftJourney = $state(null);
  let canvasKey = $state(0);

  let edgeStyle = $state('curved');
  let tidyFn = $state(null);
  let exportOpen = $state(false);
  let exportText = $state('');

  let perspectiveTab = $state('intent');
  let activePageIds = $state([]);
  let activeButtonIds = $state([]);
  let allPagesForFilter = $state([]);
  let allButtonsForFilter = $state([]);
  let editPageId = $state(null);

  async function loadFilterOptions() {
    const [pgs, btns] = await Promise.all([
      supabase.from('bb_tech_pages').select('id, title').eq('workspace_id', workspace.id).is('deleted_at', null).order('title'),
      supabase.from('bb_tech_buttons').select('id, name, page_id').eq('workspace_id', workspace.id).is('deleted_at', null).order('name')
    ]);
    allPagesForFilter = pgs.data || [];
    allButtonsForFilter = btns.data || [];
  }

  $effect(() => {
    if (workspace.id) loadFilterOptions();
  });

  function clearAllFilters() {
    activeJourneyIds = [];
    activePageIds = [];
    activeButtonIds = [];
    pickerOpen = false;
  }

  function removePill(type, id) {
    if (type === 'intent') activeJourneyIds = activeJourneyIds.filter(x => x !== id);
    if (type === 'page') activePageIds = activePageIds.filter(x => x !== id);
    if (type === 'button') activeButtonIds = activeButtonIds.filter(x => x !== id);
  }

  function toggleSelection(type, id) {
    if (type === 'intent') {
      activeJourneyIds = activeJourneyIds.includes(id) ? activeJourneyIds.filter(x => x !== id) : [...activeJourneyIds, id];
    } else if (type === 'page') {
      activePageIds = activePageIds.includes(id) ? activePageIds.filter(x => x !== id) : [...activePageIds, id];
    } else if (type === 'button') {
      activeButtonIds = activeButtonIds.includes(id) ? activeButtonIds.filter(x => x !== id) : [...activeButtonIds, id];
    }
  }

  function pillLabel(type, id) {
    if (type === 'intent') return journeys.find(j => j.id === id)?.name || '?';
    if (type === 'page') return allPagesForFilter.find(p => p.id === id)?.title || '?';
    if (type === 'button') {
      const b = allButtonsForFilter.find(b => b.id === id);
      if (!b) return '?';
      const page = allPagesForFilter.find(p => p.id === b.page_id);
      return `${b.name}${page ? ' · ' + page.title : ''}`;
    }
  }

  async function openExport() {
    const [pgs, btns, chs] = await Promise.all([
      supabase.from('bb_tech_pages').select('id, title').eq('workspace_id', workspace.id).is('deleted_at', null),
      supabase.from('bb_tech_buttons').select('id, page_id, name, is_hidden').eq('workspace_id', workspace.id).is('deleted_at', null).order('sort_order'),
      supabase.from('bb_tech_choices').select('*').eq('workspace_id', workspace.id).is('deleted_at', null)
    ]);
    const allPages = pgs.data || [];
    const allButtons = btns.data || [];
    const allChoices = chs.data || [];

    const lines = [];
    lines.push(`# ${workspace.name || 'Venture'} — Vibe Code Export`);
    lines.push('');
    lines.push(`This is a tech map of ${allPages.length} pages, ${allButtons.length} buttons, and ${allChoices.length} routes across ${journeys.length} user journeys.`);
    lines.push('');

    if (journeys.length > 0) {
      lines.push('## User Journeys');
      for (const j of journeys) {
        lines.push(`- **${j.name}** — color ${j.color}, status: ${j.status}`);
      }
      lines.push('');
    }

    lines.push('## Pages');
    for (const p of allPages) {
      const pageButtons = allButtons.filter(b => b.page_id === p.id && !b.is_hidden);
      lines.push(`### ${p.title}`);
      if (pageButtons.length === 0) {
        lines.push('_No buttons (terminal page — value delivered here)_');
      } else {
        for (const b of pageButtons) {
          const routes = allChoices.filter(c => c.source_button_id === b.id);
          if (routes.length === 0) {
            lines.push(`- **${b.name}** → (no route)`);
          } else {
            for (const r of routes) {
              const target = allPages.find(pp => pp.id === r.target_page_id);
              const journey = journeys.find(jj => jj.id === r.journey_id);
              lines.push(`- **${b.name}** → ${target?.title || '?'} _(in journey: ${journey?.name || '?'})_`);
            }
          }
        }
      }
      lines.push('');
    }

    exportText = lines.join('\n');
    exportOpen = true;
  }

  async function copyExport() {
    try {
      await navigator.clipboard.writeText(exportText);
      showToast('Copied to clipboard', 'success');
    } catch {
      showToast('Copy failed — select and copy manually', 'error');
    }
  }

  async function loadTech() {
    if (!workspace.id) return;
    loading = true;
    try {
      const [pgs, jys] = await Promise.all([
        supabase.from('bb_tech_pages').select('id, title').eq('workspace_id', workspace.id).is('deleted_at', null),
        supabase.from('bb_tech_journeys').select('id, name, audience_id, color, status, starting_page_id').eq('workspace_id', workspace.id).is('deleted_at', null)
      ]);
      pages = pgs.data || [];
      journeys = jys.data || [];
    } catch (e) {
      showToast('Tech load failed: ' + e.message, 'error');
    } finally { loading = false; }
  }

  $effect(() => {
    if (workspace.id) loadTech();
  });

  function startJourney() {
    editorOpen = true;
  }

  function mergePages() {
    showToast('Merge with AI — coming soon', 'info');
  }

  async function confirmDraft() {
    if (!draftJourney) return;
    await supabase.from('bb_tech_journeys').update({ status: 'confirmed' }).eq('id', draftJourney.id);
    showToast('Journey confirmed', 'success');
    draftJourney = null;
    await loadTech();
    canvasKey++;
  }

  function editDraft() {
    if (!draftJourney) return;
    showToast('Reopen edit flow coming in next commit — for now, build a new journey or edit on the map directly', 'info');
    draftJourney = null;
  }
</script>

<div class="tech-shell">
  {#if loading}
    <div class="tech-loading">Loading tech map…</div>
  {:else if pages.length === 0 && journeys.length === 0}
    <div class="tech-empty">
      <div class="tech-empty-inner">
        <h2>Tech mode</h2>
        <p>Map your product the way it actually works — pages, buttons, choices. Each journey is one user's intent through your app.</p>
        <button class="tech-start-btn" onclick={startJourney} type="button">
          Start journey
        </button>
      </div>
    </div>
  {:else}
    <div class="tech-toolbar">
      <button class="tech-tool-btn" onclick={startJourney} type="button">+ New journey</button>
      <button class="tech-tool-btn" class:active={pickerOpen} onclick={() => pickerOpen = !pickerOpen} type="button">
        Perspective{activeJourneyIds.length > 0 ? ` (${activeJourneyIds.length})` : ''}
      </button>
      <button class="tech-tool-btn" onclick={clearAllFilters} type="button">View all</button>
      <button class="tech-tool-btn" onclick={() => edgeStyle = edgeStyle === 'curved' ? 'right-angle' : 'curved'} type="button">
        Edges: {edgeStyle === 'curved' ? 'Curved' : 'Right-angle'}
      </button>
      <button class="tech-tool-btn" onclick={() => tidyFn?.()} type="button" disabled={!tidyFn}>
        Tidy
      </button>
      <button class="tech-tool-btn" onclick={openExport} type="button">
        📋 Vibe code export
      </button>
      <div class="tech-toolbar-spacer"></div>
      <button class="tech-tool-btn tech-merge-btn" onclick={mergePages} type="button">⚡ Merge pages with AI</button>
    </div>

    {#if pickerOpen}
      <div class="tech-picker">
        <div class="tech-picker-tabs">
          <button class="tech-picker-tab" class:active={perspectiveTab === 'intent'} onclick={() => perspectiveTab = 'intent'} type="button">Intent</button>
          <button class="tech-picker-tab" class:active={perspectiveTab === 'page'} onclick={() => perspectiveTab = 'page'} type="button">Page</button>
          <button class="tech-picker-tab" class:active={perspectiveTab === 'button'} onclick={() => perspectiveTab = 'button'} type="button">Button</button>
        </div>

        {#if activeJourneyIds.length || activePageIds.length || activeButtonIds.length}
          <div class="tech-picker-pills">
            {#each activeJourneyIds as id}
              <span class="tech-pill tech-pill-intent">{pillLabel('intent', id)} <button onclick={() => removePill('intent', id)} type="button">×</button></span>
            {/each}
            {#each activePageIds as id}
              <span class="tech-pill tech-pill-page">{pillLabel('page', id)} <button onclick={() => removePill('page', id)} type="button">×</button></span>
            {/each}
            {#each activeButtonIds as id}
              <span class="tech-pill tech-pill-button">{pillLabel('button', id)} <button onclick={() => removePill('button', id)} type="button">×</button></span>
            {/each}
            <button class="tech-pill-clear" onclick={clearAllFilters} type="button">Clear all</button>
          </div>
        {/if}

        <div class="tech-picker-list">
          {#if perspectiveTab === 'intent'}
            {#each journeys as j}
              <label class="tech-picker-row">
                <input type="checkbox" checked={activeJourneyIds.includes(j.id)} onchange={() => toggleSelection('intent', j.id)} />
                <span class="tech-picker-color" style="background: {j.color}"></span>
                <span class="tech-picker-name">{j.name}</span>
                <span class="tech-picker-status">{j.status}</span>
              </label>
            {/each}
            {#if journeys.length === 0}<div class="tech-picker-empty">No journeys yet</div>{/if}
          {:else if perspectiveTab === 'page'}
            {#each allPagesForFilter as p}
              <label class="tech-picker-row">
                <input type="checkbox" checked={activePageIds.includes(p.id)} onchange={() => toggleSelection('page', p.id)} />
                <span class="tech-picker-name">{p.title}</span>
              </label>
            {/each}
            {#if allPagesForFilter.length === 0}<div class="tech-picker-empty">No pages yet</div>{/if}
          {:else if perspectiveTab === 'button'}
            {#each allButtonsForFilter as b}
              <label class="tech-picker-row">
                <input type="checkbox" checked={activeButtonIds.includes(b.id)} onchange={() => toggleSelection('button', b.id)} />
                <span class="tech-picker-name">{b.name}</span>
                <span class="tech-picker-status">{allPagesForFilter.find(p => p.id === b.page_id)?.title || '?'}</span>
              </label>
            {/each}
            {#if allButtonsForFilter.length === 0}<div class="tech-picker-empty">No buttons yet</div>{/if}
          {/if}
        </div>
      </div>
    {/if}

    {#if draftJourney}
      <div class="tech-confirm-bar">
        <span class="tech-confirm-label">
          <span class="tech-confirm-dot" style="background: {draftJourney.color}"></span>
          Preview: {draftJourney.name} — looks right?
        </span>
        <div class="tech-confirm-actions">
          <button class="tech-tool-btn" onclick={editDraft} type="button">Edit</button>
          <button class="tech-tool-btn tech-confirm-yes" onclick={confirmDraft} type="button">Confirm</button>
        </div>
      </div>
    {/if}

    <div class="tech-canvas">
      {#key canvasKey}
        <TechCanvas
          activeJourneyIds={activeJourneyIds}
          activePageIds={activePageIds}
          activeButtonIds={activeButtonIds}
          edgeStyle={edgeStyle}
          registerTidy={(fn) => tidyFn = fn}
          oneditpage={(pageId) => editPageId = pageId}
        />
      {/key}
    </div>
  {/if}
</div>

{#if editorOpen}
  <TechJourneyEditor
    onclose={() => { editorOpen = false; loadTech(); canvasKey++; }}
    oncomplete={(journey) => {
      editorOpen = false;
      draftJourney = journey;
      activeJourneyIds = journey ? [journey.id] : [];
      loadTech();
      canvasKey++;
    }}
  />
{/if}

{#if editPageId}
  <TechJourneyEditor
    editPageId={editPageId}
    onclose={() => { editPageId = null; loadTech(); loadFilterOptions(); canvasKey++; }}
    oncomplete={() => { editPageId = null; loadTech(); loadFilterOptions(); canvasKey++; }}
  />
{/if}

{#if exportOpen}
  <div class="tech-export-overlay" onclick={() => exportOpen = false}>
    <div class="tech-export-modal" onclick={(e) => e.stopPropagation()}>
      <header class="tech-export-header">
        <h3>Vibe code export</h3>
        <button class="tech-export-x" onclick={() => exportOpen = false} type="button">×</button>
      </header>
      <p class="tech-export-hint">Paste this into Claude Code at the start of a session. It tells Claude what your app does so you skip the discovery rounds.</p>
      <textarea class="tech-export-text" readonly>{exportText}</textarea>
      <footer class="tech-export-footer">
        <button class="tech-tool-btn" onclick={copyExport} type="button">Copy</button>
        <button class="tech-tool-btn" onclick={() => exportOpen = false} type="button">Close</button>
      </footer>
    </div>
  </div>
{/if}

<style>
  .tech-shell {
    width: 100%; height: 100%;
    display: flex; flex-direction: column;
    background: #0a0a0c;
  }
  .tech-loading {
    display: flex; align-items: center; justify-content: center;
    height: 100%;
    color: #9aa0a6;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 1.2px;
  }
  .tech-empty {
    display: flex; align-items: center; justify-content: center;
    height: 100%;
    padding: 40px;
  }
  .tech-empty-inner {
    max-width: 480px;
    text-align: center;
  }
  .tech-empty-inner h2 {
    font-family: 'DM Serif Display', serif;
    font-size: 32px;
    color: #f0efe9;
    margin: 0 0 14px;
  }
  .tech-empty-inner p {
    color: #9aa0a6;
    font-size: 14px;
    line-height: 1.6;
    margin: 0 0 28px;
  }
  .tech-start-btn {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
    padding: 12px 28px;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 6px 18px rgba(212,175,55,0.3);
  }
  .tech-start-btn:hover {
    transform: scale(1.04);
    box-shadow: 0 8px 24px rgba(212,175,55,0.45);
  }
  .tech-toolbar {
    display: flex; gap: 8px; align-items: center;
    padding: 10px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    background: rgba(255,255,255,0.01);
  }
  .tech-tool-btn {
    background: rgba(255,255,255,0.04);
    color: #d8d8dc;
    border: 1px solid rgba(255,255,255,0.08);
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    transition: all 0.15s ease;
  }
  .tech-tool-btn:hover:not(:disabled) {
    background: rgba(212,175,55,0.12);
    border-color: rgba(212,175,55,0.3);
    color: #d4af37;
  }
  .tech-tool-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  .tech-tool-btn.active {
    background: rgba(212,175,55,0.18);
    border-color: rgba(212,175,55,0.4);
    color: #d4af37;
  }
  .tech-merge-btn {
    margin-left: auto;
    background: rgba(212,175,55,0.06);
    border-color: rgba(212,175,55,0.25);
    color: #d4af37;
  }
  .tech-toolbar-spacer { flex: 1; }
  .tech-canvas {
    flex: 1;
    position: relative;
    overflow: hidden;
    background: radial-gradient(circle at center, rgba(212,175,55,0.02), transparent 60%);
  }

  .tech-picker {
    position: absolute;
    top: 56px;
    left: 16px;
    width: 300px;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 12px;
    padding: 10px;
    z-index: 10;
    box-shadow: 0 12px 30px rgba(0,0,0,0.5);
    max-height: 400px;
    overflow-y: auto;
  }
  .tech-picker-header {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: #9aa0a6;
    padding: 4px 6px 8px;
  }
  .tech-picker-row {
    display: flex; align-items: center; gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    color: #d8d8dc;
  }
  .tech-picker-row:hover { background: rgba(212,175,55,0.06); }
  .tech-picker-color { width: 10px; height: 10px; border-radius: 50%; }
  .tech-picker-name { flex: 1; }
  .tech-picker-status {
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    color: #6a6a6e;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  .tech-picker-empty {
    color: #6a6a6e;
    font-size: 11px;
    padding: 10px;
    text-align: center;
  }
  .tech-picker-tabs {
    display: flex; gap: 4px;
    padding: 6px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .tech-picker-tab {
    flex: 1;
    background: transparent;
    color: #9aa0a6;
    border: 1px solid rgba(255,255,255,0.08);
    padding: 5px 10px;
    border-radius: 6px;
    cursor: pointer;
    font-family: 'JetBrains Mono', monospace;
    font-size: 9px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
  }
  .tech-picker-tab.active {
    background: rgba(212,175,55,0.15);
    border-color: rgba(212,175,55,0.35);
    color: #d4af37;
  }
  .tech-picker-pills {
    display: flex; flex-wrap: wrap; gap: 4px;
    padding: 8px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .tech-pill {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 6px 3px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    background: rgba(212,175,55,0.12);
    border: 1px solid rgba(212,175,55,0.3);
    color: #d4af37;
  }
  .tech-pill-page {
    background: rgba(122,184,212,0.12);
    border-color: rgba(122,184,212,0.3);
    color: #7AB8D4;
  }
  .tech-pill-button {
    background: rgba(201,123,181,0.12);
    border-color: rgba(201,123,181,0.3);
    color: #C97BB5;
  }
  .tech-pill button {
    background: rgba(255,255,255,0.1); color: inherit;
    border: none;
    width: 14px; height: 14px; border-radius: 50%;
    cursor: pointer; font-size: 9px; line-height: 1; padding: 0;
  }
  .tech-pill-clear {
    background: none; color: #6a6a6e;
    border: none;
    font-size: 10px;
    cursor: pointer;
    text-decoration: underline;
    font-family: 'JetBrains Mono', monospace;
  }
  .tech-pill-clear:hover { color: #d48484; }
  .tech-picker-list {
    max-height: 280px;
    overflow-y: auto;
    padding: 6px;
  }

  .tech-confirm-bar {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 16px;
    background: rgba(212,175,55,0.1);
    border-bottom: 1px solid rgba(212,175,55,0.3);
  }
  .tech-confirm-label {
    display: flex; align-items: center; gap: 8px;
    color: #f0efe9;
    font-size: 13px;
  }
  .tech-confirm-dot { width: 10px; height: 10px; border-radius: 50%; }
  .tech-confirm-actions { display: flex; gap: 6px; }
  .tech-confirm-yes {
    background: linear-gradient(180deg, #d4af37, #b8941f);
    color: #0a0a0c;
    border: none;
  }
  .tech-confirm-yes:hover { background: linear-gradient(180deg, #e0bb45, #c4a02b); }

  .tech-export-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.7);
    display: flex; align-items: center; justify-content: center;
    z-index: 200;
  }
  .tech-export-modal {
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.3);
    border-radius: 14px;
    width: 720px; max-width: 95vw; max-height: 88vh;
    display: flex; flex-direction: column;
    box-shadow: 0 24px 60px rgba(0,0,0,0.7);
  }
  .tech-export-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 14px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .tech-export-header h3 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 18px; color: #f0efe9; }
  .tech-export-x {
    background: rgba(255,255,255,0.06); color: #b8b8bc;
    border: 1px solid rgba(255,255,255,0.1);
    width: 28px; height: 28px; border-radius: 50%;
    cursor: pointer; font-size: 16px;
  }
  .tech-export-hint {
    padding: 12px 18px 0;
    margin: 0;
    color: #9aa0a6;
    font-size: 12px;
    line-height: 1.5;
  }
  .tech-export-text {
    flex: 1;
    margin: 12px 18px;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 12px;
    border-radius: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    line-height: 1.6;
    resize: none;
    min-height: 280px;
  }
  .tech-export-footer {
    display: flex; gap: 8px; justify-content: flex-end;
    padding: 12px 18px;
    border-top: 1px solid rgba(255,255,255,0.06);
  }
</style>
